import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  TreeStructure, Plus, PencilSimple, TrashSimple, CaretRight, CaretDown,
  TrendUp, TrendDown, Lock, MagnifyingGlass, Copy, Folder, FolderOpen,
  Buildings, CaretLeft, Sparkle, ArrowsClockwise, MapPin, Star, StarFour,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

const empty = {
  code: "", name: "", description: "",
  kind: "gider", parent_code: "",
  accounting_code: "", kdv_rate: 20,
  is_active: true, sort_order: 100,
};
const emptyTpl = { key: "", name: "", description: "" };

export default function BudgetCBS() {
  const { sites, activeSiteId, setActiveSiteId } = useSites();
  const [templates, setTemplates] = useState([]);
  const [activeTpl, setActiveTpl] = useState("standart");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [tplForm, setTplForm] = useState(emptyTpl);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");
  // Site-aware CBS state
  const [siteCbsInfo, setSiteCbsInfo] = useState(null);
  const [sitesCbsMap, setSitesCbsMap] = useState({});   // { siteId: cbsInfo }
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignKey, setAssignKey] = useState("");
  // Şablon drill-down modu — bir şablon kartına tıklayınca aktifleşir
  const [focusedTemplate, setFocusedTemplate] = useState(null);
  const confirm = useConfirm();

  const isSiteMode = activeSiteId && activeSiteId !== "all";
  const isTemplateFocus = !isSiteMode && !!focusedTemplate;
  const currentSite = useMemo(
    () => sites.find((s) => s.id === activeSiteId),
    [sites, activeSiteId]
  );

  const loadTemplates = async () => { setTemplates([]); }; // Faz 34.52: template sistemi kaldırıldı
  const load = async () => {
    if (!isSiteMode) { setRows([]); return; }
    try {
      const { data } = await axios.get(`${API}/budget/categories`, {
        params: { site_id: activeSiteId },
      });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reload = async () => { await load(); };
  useEffect(() => { load(); }, [activeSiteId]); // eslint-disable-line

  // Site modu: aktif şantiye değişince özet bilgiyi getir
  useEffect(() => {
    if (!isSiteMode) { setSiteCbsInfo(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sites/${activeSiteId}/cbs-info`);
        setSiteCbsInfo(data);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [activeSiteId, isSiteMode]);

  // Landing: tüm şantiyelerin CBS özetlerini paralel çek
  useEffect(() => {
    if (isSiteMode || !sites?.length) return;
    (async () => {
      const results = await Promise.all(
        sites.map(async (s) => {
          try {
            const { data } = await axios.get(`${API}/sites/${s.id}/cbs-info`);
            return [s.id, data];
          } catch { return [s.id, null]; }
        })
      );
      setSitesCbsMap(Object.fromEntries(results));
    })();
  }, [isSiteMode, sites]);

  // Faz 34.52: Şablon atama & kişiselleştirme kaldırıldı — no-op
  const assignTemplate = async () => {};
  const personalizeSiteCbs = async () => {};

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = (parentCode = "") => {
    setEditing(null); setForm({ ...empty, parent_code: parentCode }); setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      code: r.code, name: r.name || "", description: r.description || "",
      kind: r.kind || "gider", parent_code: r.parent_code || "",
      accounting_code: r.accounting_code || "",
      kdv_rate: r.kdv_rate ?? 20,
      is_active: r.is_active !== false,
      sort_order: r.sort_order ?? 100,
    });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (!isSiteMode) { toast.error("Önce bir şantiye seçin"); return; }
      const payload = {
        ...form, site_id: activeSiteId,
        kdv_rate: parseFloat(form.kdv_rate) || 0,
        sort_order: parseInt(form.sort_order, 10) || 100,
      };
      if (editing) {
        await axios.put(`${API}/budget/categories/${editing.id}`, payload);
        toast.success("Kategori güncellendi");
      } else {
        await axios.post(`${API}/budget/categories`, payload);
        toast.success("Kategori eklendi");
      }
      setOpen(false); reload();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    // Alt düğümleri özyinelemeli say — sistem düğümü olanları hariç tut
    const descendants = [];
    const queue = [r.code];
    while (queue.length) {
      const code = queue.shift();
      rows.forEach((x) => {
        if (x.parent_code === code && !x.is_system) {
          descendants.push(x); queue.push(x.code);
        }
      });
    }
    const count = descendants.length;
    const ok = await confirm({
      title: count > 0 ? "Kök Düğüm ve Alt Kırınımları Sil" : "Kategoriyi Sil",
      description: count > 0
        ? `"${r.code} — ${r.name}" ve altındaki ${count} kalem birlikte silinecek. Bu kalemlere kaydedilmiş gider/gelir kayıtları SİLİNMEZ, "Atanmamış" konumuna düşer. Silme sonrası 5 saniyeliğine "Geri Al" seçeneği görüntülenecek.`
        : `${r.code} — ${r.name} kalıcı olarak silinsin mi? Bu kaleme kaydedilmiş gider/gelir kayıtları SİLİNMEZ, "Atanmamış" konumuna düşer. Silme sonrası 5 saniyeliğine "Geri Al" seçeneği görüntülenecek.`,
      confirmText: count > 0 ? `Sil (${count + 1} kalem)` : "Sil",
      variant: "destructive",
    });
    if (!ok) return;

    try {
      const { data } = await axios.delete(`${API}/budget/categories/${r.id}`, {
        params: { cascade: count > 0 },
      });
      const total = data.deleted_count || 1;
      const restoreItems = data.restore || [];
      const detached = data.restore_entries || [];
      reload();
      const label = detached.length
        ? `${total} kalem silindi · ${detached.length} kayıt "Atanmamış"a düştü`
        : total > 1 ? `${total} kalem silindi` : "Kategori silindi";
      toast.success(label, {
        duration: 5000,
        action: (restoreItems.length || detached.length) ? {
          label: "Geri Al",
          onClick: async () => {
            try {
              const { data: r2 } = await axios.post(
                `${API}/budget/categories/restore`,
                { items: restoreItems, entries: detached });
              toast.success(`${r2.restored} kalem${r2.restored_entries ? ` + ${r2.restored_entries} kayıt yeniden bağlandı` : ""}`);
              reload();
            } catch (e) { toast.error(formatErr(e)); }
          },
        } : undefined,
      });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Şablon işlemleri
  const openNewTpl = () => { setTplEditing(null); setTplForm(emptyTpl); setTplOpen(true); };
  const openEditTpl = () => {
    const t = templates.find((x) => x.key === activeTpl);
    if (!t) return;
    setTplEditing(t);
    setTplForm({ key: t.key, name: t.name || "", description: t.description || "" });
    setTplOpen(true);
  };
  const saveTpl = async () => {
    try {
      if (tplEditing) {
        await axios.put(`${API}/budget/templates/${tplEditing.key}`, { ...tplForm, key: tplEditing.key });
        toast.success("Şablon güncellendi");
      } else {
        await axios.post(`${API}/budget/templates`, tplForm);
        toast.success("Boş şablon oluşturuldu — kategorileri ekleyin veya klonlayın");
      }
      setTplOpen(false); loadTemplates();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeTpl = async (tplKey) => {
    const key = tplKey || activeTpl;
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    const cats = t.category_count || 0;
    const ok = await confirm({
      title: "Şablonu Sil",
      description: cats > 0
        ? `"${t.name}" şablonu ve içindeki ${cats} kırınım kalemi tamamen silinecek. Devam edilsin mi?`
        : `"${t.name}" şablonu silinecek. Devam edilsin mi?`,
      confirmText: cats > 0 ? `Sil (${cats} kalem dahil)` : "Sil",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/budget/templates/${key}`, { params: { cascade: true } });
      toast.success(cats > 0 ? `Şablon ve ${cats} kırınım silindi` : "Şablon silindi");
      if (activeTpl === key) setActiveTpl("standart");
      if (focusedTemplate === key) setFocusedTemplate(null);
      loadTemplates();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const doClone = async () => {
    try {
      const { data } = await axios.post(
        `${API}/budget/templates/${activeTpl}/clone`,
        { name: cloneName || `${templates.find((t) => t.key === activeTpl)?.name || ""} (Kopya)` }
      );
      toast.success(`"${data.name}" oluşturuldu — ${data.cloned_categories} kategori kopyalandı`);
      setCloneOpen(false); setCloneName("");
      await loadTemplates();
      setActiveTpl(data.key);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const tree = useMemo(() => {
    const byParent = {};
    const filtered = rows.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (q) {
        const nq = q.toLowerCase();
        return (r.code || "").toLowerCase().includes(nq)
            || (r.name || "").toLowerCase().includes(nq)
            || (r.accounting_code || "").toLowerCase().includes(nq);
      }
      return true;
    });
    let visibleCodes = new Set(filtered.map((r) => r.code));
    if (q) {
      const codeToParent = {};
      rows.forEach((r) => { codeToParent[r.code] = r.parent_code; });
      filtered.forEach((r) => {
        let p = r.parent_code;
        while (p) { visibleCodes.add(p); p = codeToParent[p] || ""; }
      });
    }
    const finalRows = rows.filter((r) => visibleCodes.has(r.code) &&
      (kindFilter === "all" || r.kind === kindFilter));
    // Şablonda gerçekten var olan kodların seti — parent_code buna ait değilse
    // düğüm "orphan" (yetim) sayılır ve tepeden gösterilir. Aksi halde kalem
    // ekranda gizlenirdi (Faz 34.42 fix: Deneme 4 şablonu).
    const existingCodes = new Set(rows.map((r) => r.code));
    finalRows.forEach((r) => {
      const rawParent = r.parent_code || "";
      const isOrphan = rawParent && !existingCodes.has(rawParent);
      const p = !rawParent || isOrphan ? "__root__" : rawParent;
      (byParent[p] = byParent[p] || []).push(r);
    });
    // Sıralama (Faz 34.53): Gelirler daima en üstte, giderler altta.
    // Her seviyede kind önce (gelir=0, gider=1), sonra doğal alfabetik kod sıralaması.
    // Proje Yapısı ile birebir aynı davranış.
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => {
      const kindA = a.kind === "gelir" ? 0 : 1;
      const kindB = b.kind === "gelir" ? 0 : 1;
      if (kindA !== kindB) return kindA - kindB;
      return (a.code || "").localeCompare(b.code || "", "tr", {
        numeric: true, sensitivity: "base",
      });
    }));
    return byParent;
  }, [rows, q, kindFilter]);

  const stats = useMemo(() => ({
    total: rows.length,
    gelir: rows.filter((r) => r.kind === "gelir").length,
    gider: rows.filter((r) => r.kind === "gider").length,
    system: rows.filter((r) => r.is_system).length,
    custom: rows.length - rows.filter((r) => r.is_system).length,
  }), [rows]);

  const activeTemplate = templates.find((t) => t.key === activeTpl);

  // --- Sürükle-Bırak (Reparent) ---
  const [draggingCode, setDraggingCode] = useState(null);
  const [dropTargetCode, setDropTargetCode] = useState(null); // code veya "__root__"

  const cbsDescendantSet = useMemo(() => {
    if (!draggingCode) return new Set();
    const set = new Set([draggingCode]);
    const walk = (parent) => {
      for (const r of rows) {
        if (r.parent_code === parent && !set.has(r.code)) {
          set.add(r.code);
          walk(r.code);
        }
      }
    };
    walk(draggingCode);
    return set;
  }, [draggingCode, rows]);

  const draggingNode = useMemo(
    () => (draggingCode ? rows.find((r) => r.code === draggingCode) : null),
    [draggingCode, rows]
  );

  const reparentCbs = async (code, newParentCode) => {
    if (!code) return;
    const child = rows.find((r) => r.code === code);
    if (!child) return;
    if ((child.parent_code || "") === (newParentCode || "")) return;
    if (child.is_system) {
      toast.error("Sistem düğümü taşınamaz");
      return;
    }
    if (newParentCode && cbsDescendantSet.has(newParentCode)) {
      toast.error("Kalem kendi alt düğümüne taşınamaz");
      return;
    }
    if (newParentCode) {
      const parent = rows.find((r) => r.code === newParentCode);
      if (parent && (parent.kind || "gider") !== (child.kind || "gider")) {
        toast.error("Kalem türü (Gelir/Gider) ana düğümle uyuşmalı");
        return;
      }
    }
    try {
      await axios.post(`${API}/budget/categories/${child.id}/reparent`, {
        parent_code: newParentCode || null,
      });
      const label = newParentCode
        ? (rows.find((r) => r.code === newParentCode)?.name || newParentCode)
        : "Kök seviye";
      toast.success(`"${child.name}" taşındı → ${label}`);
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onCbsDragStart = (code) => (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", code);
    setDraggingCode(code);
  };
  const onCbsDragEnd = () => { setDraggingCode(null); setDropTargetCode(null); };
  const onCbsDragOver = (code) => (e) => {
    if (!draggingCode) return;
    if (cbsDescendantSet.has(code)) return;
    // Kind uyumsuzsa hover feedback verme (drop üyeliği reddedilir)
    const nodeK = (rows.find((r) => r.code === code)?.kind || "gider");
    const dragK = (draggingNode?.kind || "gider");
    if (nodeK !== dragK) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetCode !== code) setDropTargetCode(code);
  };
  const onCbsDrop = (code) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const c = e.dataTransfer.getData("text/plain") || draggingCode;
    setDraggingCode(null); setDropTargetCode(null);
    reparentCbs(c, code);
  };
  const onCbsRootDragOver = (e) => {
    if (!draggingCode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetCode !== "__root__") setDropTargetCode("__root__");
  };
  const onCbsRootDrop = (e) => {
    e.preventDefault();
    const c = e.dataTransfer.getData("text/plain") || draggingCode;
    setDraggingCode(null); setDropTargetCode(null);
    reparentCbs(c, null);
  };

  const renderNode = (node, depth) => {
    const kids = tree[node.code] || [];
    const isCollapsed = collapsed[node.code];
    const isDragging = draggingCode === node.code;
    const isDropTarget = dropTargetCode === node.code;
    const isForbidden = draggingCode && (
      cbsDescendantSet.has(node.code) ||
      (draggingNode && (draggingNode.kind || "gider") !== (node.kind || "gider"))
    );
    // Yetim (orphan): parent_code var ama bu şablonda o kod yok
    const isOrphan = !!node.parent_code &&
      !rows.some((r) => r.code === node.parent_code);
    return (
      <React.Fragment key={node.id || node.code}>
        <tr
          data-testid={`cbs-node-${node.code}`}
          draggable={!node.is_system}
          onDragStart={!node.is_system ? onCbsDragStart(node.code) : undefined}
          onDragEnd={onCbsDragEnd}
          onDragOver={onCbsDragOver(node.code)}
          onDragLeave={() => setDropTargetCode((cur) => (cur === node.code ? null : cur))}
          onDrop={onCbsDrop(node.code)}
          className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
            isDragging ? "opacity-40" : ""
          } ${isDropTarget ? "bg-[#F7FBE9] ring-2 ring-[#95C11F]" : ""} ${
            isForbidden ? "opacity-50 cursor-not-allowed" : ""
          } ${!node.is_system ? "cursor-grab active:cursor-grabbing" : ""}`}
        >
          <td className="px-4 py-2.5" style={{ paddingLeft: 16 + depth * 20 }}>
            <div className="flex items-center gap-2">
              {kids.length > 0 ? (
                <button onClick={() => setCollapsed((c) => ({ ...c, [node.code]: !isCollapsed }))}
                        className="text-slate-400 hover:text-[#1B4776]" data-testid={`toggle-${node.code}`}>
                  {isCollapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                </button>
              ) : (<span className="w-3.5" />)}
              <span className={`font-mono text-xs font-bold ${node.kind === "gelir" ? "text-emerald-700" : "text-[#1B4776]"}`}>{node.code}</span>
              {node.is_system && <Lock size={10} weight="fill" className="text-slate-300" title="Sistem düğümü (taşınamaz)" />}
              {isOrphan && (
                <span
                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200"
                  title={`Ana kod "${node.parent_code}" bu şablonda tanımlı değil. Düzenle > Ana Kod'u değiştirin ya da bu kalemi sürükleyerek başka bir ana altına taşıyın.`}
                  data-testid={`orphan-${node.code}`}
                >
                  Yetim
                </span>
              )}
            </div>
          </td>
          <td className="px-4 py-2.5 font-medium text-slate-800">
            {node.name}
            {node.description && <div className="text-[10px] text-slate-400 mt-0.5">{node.description}</div>}
          </td>
          <td className="px-4 py-2.5 text-center">
            <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
              node.kind === "gelir"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-rose-50 text-rose-700 border-rose-200"
            }`}>
              {node.kind === "gelir" ? <TrendUp size={9} weight="bold" /> : <TrendDown size={9} weight="bold" />}
              {node.kind === "gelir" ? "Gelir" : "Gider"}
            </span>
          </td>
          <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{node.accounting_code || "—"}</td>
          <td className="px-4 py-2.5 text-right">
            <div className="inline-flex items-center gap-1">
              <button onClick={() => openNew(node.code)} className="p-1 text-slate-400 hover:text-[#95C11F]"
                      title="Alt düğüm ekle" data-testid={`add-child-${node.code}`}>
                <Plus size={14} weight="bold" />
              </button>
              <button onClick={() => openEdit(node)} className="p-1 text-slate-400 hover:text-[#1B4776]"
                      title="Düzenle" data-testid={`edit-${node.code}`}>
                <PencilSimple size={14} weight="bold" />
              </button>
              {!node.is_system && (
                <button onClick={() => remove(node)} className="p-1 text-slate-400 hover:text-rose-600"
                        title="Sil" data-testid={`del-${node.code}`}>
                  <TrashSimple size={14} weight="bold" />
                </button>
              )}
            </div>
          </td>
        </tr>
        {!isCollapsed && kids.map((k) => renderNode(k, depth + 1))}
      </React.Fragment>
    );
  };

  const roots = tree["__root__"] || [];

  return (
    <Shell title="Maliyet Kırınım Yapısı" subtitle="Bütçe Yönetimi">
      {/* Landing: Tüm Şantiyeler modu — Her şantiyenin CBS ataması */}
      {!isSiteMode && !isTemplateFocus && (
        <CbsSiteLanding
          sites={sites}
          sitesCbsMap={sitesCbsMap}
          templates={templates}
          onSelectSite={(sid) => setActiveSiteId(sid)}
        />
      )}

      {/* Site modu geri butonu */}
      {isSiteMode && (
        <div className="mb-3">
          <button
            onClick={() => setActiveSiteId("all")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B4776] hover:text-[#95C11F] transition-colors"
            data-testid="cbs-back-to-sites"
          >
            <CaretLeft size={14} weight="bold" />
            Tüm Şantiyelere Dön
          </button>
        </div>
      )}

      {/* Site Modu Header — Şantiyenin CBS Ağacı (Faz 34.52, şablonsuz) */}
      {isSiteMode && siteCbsInfo && (
        <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200 bg-gradient-to-r from-[#1B4776] to-[#12324f] text-white">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 bg-white/15 border border-white/30">
                <Buildings size={22} weight="duotone" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
                  Maliyet Kırınım Yapısı
                </div>
                <div className="text-lg font-bold mt-0.5">
                  <span className="font-mono text-[#95C11F]">{siteCbsInfo.site_code}</span>
                  <span className="mx-2 opacity-50">·</span>
                  {siteCbsInfo.site_name}
                </div>
                <div className="text-[11px] text-white/80 mt-1">
                  Bu şantiyeye özel CBS ağacı · <span className="font-bold text-[#F7FBE9]">{siteCbsInfo.category_count}</span> kalem
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Landing: Sadece şantiye kartları — TemplateCardsGrid kaldırıldı (Faz 34.52) */}

      {/* Şablon Kartları KALDIRILDI (Faz 34.52) — sadece şantiye kartları var */}

      {/* Stats + Toolbar + Tree — sadece site modunda göster */}
      {isSiteMode && (
        <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam Düğüm</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Gelir Kalemleri</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.gelir}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold">Gider Kalemleri</div>
          <div className="text-2xl font-bold text-rose-700 mt-1">{stats.gider}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sistem / Özel</div>
          <div className="text-lg font-bold text-slate-700 mt-1">
            <span className="text-[#1B4776]">{stats.system}</span>
            <span className="text-slate-300 mx-1.5">/</span>
            <span className="text-[#95C11F]">{stats.custom}</span>
          </div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-1">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="cbs-search"
                     placeholder="Kod, isim veya muhasebe kodu ara..."
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9 rounded-none sm:rounded-sm" />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-40 rounded-none sm:rounded-sm" data-testid="cbs-kind-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="gelir">Sadece Gelir</SelectItem>
                <SelectItem value="gider">Sadece Gider</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => openNew("")}
                  className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                  data-testid="cbs-add-root">
            <Plus size={14} weight="bold" className="mr-1.5" /> Yeni Kök Düğüm
          </Button>
        </div>
      </Card>

      {/* Tree Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 text-left w-56">Kod</th>
                <th className="px-4 py-3 text-left">Kalem Adı</th>
                <th className="px-4 py-3 text-center w-24">Tür</th>
                <th className="px-4 py-3 text-left w-32">Muhasebe Kodu</th>
                <th className="px-4 py-3 text-right w-32">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="cbs-tree-body">
              {roots.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center text-slate-400">
                    <TreeStructure size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Bu şablonda henüz Maliyet Kırınım Yapısı düğümü yok.</p>
                    <p className="text-xs mt-1">"Yeni Kök Düğüm" ile başlayın veya "Kopyala" ile başka şablondan yükleyin.</p>
                  </td>
                </tr>
              )}
              {roots.map((r) => renderNode(r, 0))}
              {draggingCode && (
                <tr>
                  <td colSpan={5} className="p-2">
                    <div
                      data-testid="cbs-root-dropzone"
                      onDragOver={onCbsRootDragOver}
                      onDragLeave={() => setDropTargetCode((cur) => (cur === "__root__" ? null : cur))}
                      onDrop={onCbsRootDrop}
                      className={`px-4 py-4 border-2 border-dashed text-center text-[10px] uppercase tracking-widest font-bold transition-colors ${
                        dropTargetCode === "__root__"
                          ? "border-[#95C11F] bg-[#F7FBE9] text-[#1B4776]"
                          : "border-slate-300 text-slate-500 bg-slate-50"
                      }`}
                    >
                      ⇧ Kök seviyeye taşımak için buraya bırakın
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-2">
          <Lock size={10} weight="fill" /> Sistem şablonları ve düğümleri silinemez — sadece düzenlenebilir.
        </span>
        <span className="normal-case tracking-normal text-slate-500 italic">
          · Bir düğümü başka düğüm üzerine sürükleyerek alt kaleme dönüştürebilirsiniz (kod cascade otomatik).
        </span>
      </div>
        </>
      )}

      {/* Kategori Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>{editing ? `Kategori Düzenle: ${editing.code}` : "Yeni Kategori"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kod *</label>
                <Input data-testid="cat-code" value={form.code}
                       onChange={(e) => set("code", e.target.value.toUpperCase())}
                       disabled={!!editing && editing.is_system}
                       placeholder="Örn: DLY.OFS.02"
                       className="rounded-none sm:rounded-sm font-mono" />
                {editing && !editing.is_system && (
                  <div className="text-[9px] text-amber-700 mt-1 leading-snug">
                    Kod değiştirilirse alt kalemlerin üst kodları ve bu kalemi kullanan bütçe girişleri
                    otomatik güncellenir.
                  </div>
                )}
                {editing?.is_system && (
                  <div className="text-[9px] text-slate-500 mt-1">
                    Sistem düğümünün kodu değiştirilemez. Ad ve açıklamayı düzenleyebilirsiniz.
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tür *</label>
                <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                  <SelectTrigger data-testid="cat-kind" className="rounded-none sm:rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gider">Gider</SelectItem>
                    <SelectItem value="gelir">Gelir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kalem Adı *</label>
              <Input data-testid="cat-name" value={form.name}
                     onChange={(e) => set("name", e.target.value)}
                     placeholder="Örn: Ofis Kırtasiye Giderleri"
                     className="rounded-none sm:rounded-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Üst Kategori</label>
              <Select value={form.parent_code || "__none"}
                      onValueChange={(v) => set("parent_code", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="cat-parent" className="rounded-none sm:rounded-sm">
                  <SelectValue placeholder="— Kök düğüm —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Kök düğüm (üst yok) —</SelectItem>
                  {rows.filter((r) => r.kind === form.kind && r.code !== form.code).map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      <span className="font-mono text-xs text-[#1B4776] mr-2">{r.code}</span>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Muhasebe Kodu <span className="text-slate-400 normal-case">(Zirve)</span>
              </label>
              <Input data-testid="cat-acc-code" value={form.accounting_code}
                     onChange={(e) => set("accounting_code", e.target.value)}
                     placeholder="Örn: 740.18.01.01"
                     className="rounded-none sm:rounded-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Açıklama</label>
              <Textarea data-testid="cat-desc" value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        rows={2} className="rounded-none sm:rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save} className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm" data-testid="cat-save">
              {editing ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şablon dialog'ları KALDIRILDI (Faz 34.52) */}
    </Shell>
  );
}


// ============================================================================
// CBS Site Landing — Şantiyelerin CBS atamalarını gösteren kart görünümü
// ============================================================================
function CbsSiteLanding({ sites, sitesCbsMap, templates, onSelectSite }) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let out = sites || [];
    if (q) {
      const nq = q.toLowerCase();
      out = out.filter((s) =>
        s.code?.toLowerCase().includes(nq) ||
        s.name?.toLowerCase().includes(nq)
      );
    }
    return out;
  }, [sites, q]);

  return (
    <div className="mb-4">
      {/* Üst başlık — WBS ile aynı desen: sol açıklama, sağda arama */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#95C11F]/15 flex items-center justify-center">
            <TreeStructure size={22} weight="duotone" className="text-[#95C11F]" />
          </div>
          <div>
            <div className="text-2xl font-bold text-[#1B4776]">
              Şantiyeler
            </div>
            <p className="text-sm text-slate-500">
              Maliyet Kırınım Yapısını düzenlemek için bir şantiye seçin.
            </p>
          </div>
        </div>
        <div className="relative">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Şantiye ara..."
            className="pl-9 w-72"
            data-testid="cbs-site-search"
          />
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 rounded-none sm:rounded-sm border-slate-200">
          <Buildings size={40} weight="duotone" className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Kriterlere uyan şantiye bulunamadı.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => {
            const info = sitesCbsMap[s.id];
            const isKapali = s.status === "kapali";
            const isPersonal = info?.template_is_personal;
            const isSystem = info?.template_is_system;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelectSite(s.id)}
                data-testid={`cbs-site-card-${s.code || s.id}`}
                className={`group text-left ${isKapali ? "opacity-70 hover:opacity-100" : ""}`}
              >
                <Card
                  className={`p-5 hover:shadow-lg transition-all cursor-pointer border-l-4 ${
                    isKapali
                      ? "border-l-slate-300/60 hover:border-l-slate-400 hover:border-slate-300"
                      : "border-l-[#95C11F]/40 hover:border-l-[#95C11F] hover:border-[#95C11F]"
                  }`}
                >
                  {/* Üst kısım — WBS kart stili */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-mono font-bold tracking-wider uppercase ${
                            isKapali ? "text-slate-500" : "text-[#95C11F]"
                          }`}
                        >
                          {s.code}
                        </span>
                        <span
                          className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                            isKapali
                              ? "bg-slate-100 text-slate-600 border-slate-300"
                              : "bg-emerald-50 text-emerald-700 border-emerald-300"
                          }`}
                        >
                          {isKapali ? "Kapalı" : "Aktif"}
                        </span>
                      </div>
                      <div className="mt-1 text-base font-bold text-[#0F172A] group-hover:text-[#1B4776] transition-colors line-clamp-2">
                        {s.name}
                      </div>
                      {(s.city || s.district) && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                          <MapPin size={13} weight="fill" className="text-[#1B4776]/60" />
                          <span>{[s.district, s.city].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={`p-2 rounded-full transition-colors ${
                        isKapali
                          ? "bg-slate-100 group-hover:bg-slate-300"
                          : "bg-slate-50 group-hover:bg-[#95C11F]"
                      }`}
                    >
                      <CaretRight
                        size={16}
                        weight="bold"
                        className="text-slate-400 group-hover:text-white transition-colors"
                      />
                    </div>
                  </div>

                  {/* Alt kısım — CBS özel bilgileri */}
                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                    {!info ? (
                      <div className="text-[11px] text-slate-400 italic">
                        Maliyet Kırınım Yapısı yükleniyor…
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          {isPersonal ? (
                            <Sparkle size={13} weight="fill" className="text-[#95C11F]" />
                          ) : isSystem ? (
                            <Lock size={13} weight="fill" className="text-slate-400" />
                          ) : (
                            <Folder size={13} weight="fill" className="text-[#1B4776]" />
                          )}
                          <span className="font-semibold text-slate-700 truncate flex-1">
                            {info.template_name}
                          </span>
                          {isPersonal ? (
                            <span className="shrink-0 text-[9px] uppercase tracking-widest font-bold bg-[#F7FBE9] text-[#556B10] border border-[#95C11F] px-1.5 py-0.5">
                              Özel
                            </span>
                          ) : isSystem ? (
                            <span className="shrink-0 text-[9px] uppercase tracking-widest font-bold bg-slate-100 text-slate-700 border border-slate-300 px-1.5 py-0.5">
                              Sistem
                            </span>
                          ) : (
                            <span className="shrink-0 text-[9px] uppercase tracking-widest font-bold bg-blue-50 text-[#1B4776] border border-blue-200 px-1.5 py-0.5">
                              Paylaşımlı
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <TreeStructure size={13} weight="bold" className="text-[#1B4776]/60" />
                            <span className="font-mono font-bold text-[#1B4776]">
                              {info.category_count}
                            </span>
                            <span>kalem</span>
                          </span>
                          <span className="text-slate-300">·</span>
                          <span className="group-hover:text-[#556B10] font-bold transition-colors">
                            Aç / Düzenle
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Şablon Kartları — CBS kütüphanesi için kart görünümü
// ============================================================================
function TemplateCardsGrid({
  templates, activeTpl, onSelectTpl, onNewTpl, onEditTpl, onCloneTpl,
  onDeleteTpl, onSetDefault, activeTemplate,
}) {
  return (
    <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen size={18} weight="duotone" className="text-[#95C11F]" />
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Şablonlar</div>
        </div>
        <div className="flex items-center gap-2">
          {activeTemplate && (
            <>
              <Button variant="outline" size="sm" onClick={onCloneTpl}
                      className="rounded-none sm:rounded-sm h-8" data-testid="tpl-clone-btn">
                <Copy size={12} weight="bold" className="mr-1" /> Kopyala
              </Button>
              <Button variant="outline" size="sm" onClick={onEditTpl}
                      className="rounded-none sm:rounded-sm h-8" data-testid="tpl-edit-btn"
                      disabled={activeTemplate.is_system}>
                <PencilSimple size={12} weight="bold" />
              </Button>
              <Button variant="outline" size="sm" onClick={onDeleteTpl}
                      className="rounded-none sm:rounded-sm h-8 text-rose-600 hover:bg-rose-50"
                      data-testid="tpl-del-btn"
                      disabled={activeTemplate.is_system || activeTemplate.is_default}>
                <TrashSimple size={12} weight="bold" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((t) => {
          const isActive = t.key === activeTpl;
          const isDefault = !!t.is_default;
          const isSystem = !!t.is_system;
          const isSitePersonal = !!t.site_id;
          return (
            <button key={t.key} type="button" onClick={() => onSelectTpl(t.key)}
                    data-testid={`tpl-card-${t.key}`}
                    className={`relative text-left border-2 p-0 transition-all group ${
                      isActive
                        ? "border-[#95C11F] bg-[#F7FBE9] shadow-md"
                        : "border-slate-200 bg-white hover:border-[#1B4776] hover:shadow"
                    }`}>
              {/* Hover-visible delete button — sistem ve varsayılan silinemez */}
              {!isSystem && !isDefault && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onDeleteTpl(t.key); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onDeleteTpl(t.key); }
                  }}
                  className="absolute top-2 right-2 z-10 p-1.5 bg-white/95 border border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                  title="Şablonu sil (tüm kırınımlarla birlikte)"
                  data-testid={`tpl-delete-${t.key}`}
                >
                  <TrashSimple size={12} weight="bold" />
                </div>
              )}
              {/* Header */}
              <div className={`px-3 py-2 border-b-2 flex items-center justify-between gap-2 ${
                isActive
                  ? "bg-[#95C11F] border-[#556B10] text-white"
                  : "bg-slate-50 border-slate-200"
              }`}>
                <div className="flex items-center gap-1.5 min-w-0">
                  {isSitePersonal ? (
                    <Sparkle size={12} weight="fill" className={isActive ? "text-white" : "text-[#95C11F]"} />
                  ) : isSystem ? (
                    <Lock size={12} weight="fill" className={isActive ? "text-white" : "text-slate-400"} />
                  ) : (
                    <Folder size={12} weight="fill" className={isActive ? "text-white" : "text-[#1B4776]"} />
                  )}
                  <div className={`text-sm font-bold truncate ${isActive ? "text-white" : "text-slate-800"}`}>
                    {t.name}
                  </div>
                </div>
                {isDefault && (
                  <span className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                    isActive
                      ? "bg-white text-[#556B10] border-white"
                      : "bg-amber-50 text-amber-700 border-amber-300"
                  }`}>
                    <StarFour size={9} weight="fill" /> Varsayılan
                  </span>
                )}
              </div>
              {/* Body */}
              <div className="p-3 space-y-1.5">
                {t.description && (
                  <div className="text-[11px] text-slate-500 leading-snug line-clamp-2">
                    {t.description}
                  </div>
                )}
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold pt-1">
                  <span className="text-slate-500">Kalem</span>
                  <span className={`font-mono ${isActive ? "text-[#556B10]" : "text-[#1B4776]"}`}>
                    {t.category_count ?? 0}
                  </span>
                </div>
                {!isDefault && !isSitePersonal && !isSystem && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onSetDefault(t.key); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onSetDefault(t.key); }
                    }}
                    className="w-full text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-amber-700 pt-1 flex items-center gap-1 cursor-pointer"
                    data-testid={`tpl-setdefault-${t.key}`}
                  >
                    <Star size={10} weight="bold" />
                    Varsayılan Yap
                  </div>
                )}
              </div>
            </button>
          );
        })}
        {/* Yeni Şablon Kartı */}
        <button type="button" onClick={onNewTpl}
                data-testid="tpl-new-btn"
                className="border-2 border-dashed border-slate-300 hover:border-[#95C11F] p-5 text-center text-slate-500 hover:text-[#556B10] hover:bg-[#F7FBE9] transition-all flex flex-col items-center justify-center gap-2 min-h-[130px]">
          <Plus size={22} weight="bold" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Yeni Şablon Ekle</span>
        </button>
      </div>
    </Card>
  );
}

